import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Globe,
  Copy,
  Check,
  Server,
  ExternalLink,
  Info,
  Loader2,
} from 'lucide-react';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import { InstanceTagLabel } from '@/service/gpu-instance/type';

const PROXY_BASE_URL = 'https://proxy-instance.float16.cloud';
const MIN_PORT = 3000;
const MAX_PORT = 4000;

const GPUInstanceEndpoint = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances, isLoading } = useListSSHInstances();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedPort, setSelectedPort] = useState<number>(3000);

  // Find the instance
  const instance = instances?.find((i) => i.task_id === instanceId);

  // Get the parent_task_id (original instance ID)
  const parentTaskId = instance?.parent_task_id || instance?.task_id;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format: baseUrl/task_id/port/path
  const getProxyEndpoint = (path: string, port: number) => {
    return `${PROXY_BASE_URL}/${parentTaskId}/${port}${path}`;
  };

  // Common ports for different services
  const commonPorts = [
    { port: 3000, name: 'Default', description: 'Common web server port' },
    { port: 3001, name: 'Dev Server', description: 'Development server' },
    { port: 8000, name: 'API (Not proxied)', description: 'Outside proxy range' },
    { port: 8080, name: 'HTTP Alt (Not proxied)', description: 'Outside proxy range' },
  ];

  // vLLM specific ports
  const vllmPorts = [
    { port: 3900, name: 'vLLM API', description: 'OpenAI-compatible API' },
  ];

  // Get tag-specific port suggestions
  const getTagPorts = () => {
    const tag = instance?.instance_tag;
    if (tag === 'vllm' || tag === 'ssh-vllm') return vllmPorts;
    if (tag === 'triton' || tag === 'ssh-triton') {
      return [{ port: 3000, name: 'Triton HTTP', description: 'Triton Inference Server' }];
    }
    return commonPorts.filter((p) => p.port >= MIN_PORT && p.port <= MAX_PORT);
  };

  // Set default port based on instance tag
  useEffect(() => {
    const tag = instance?.instance_tag;
    if (tag === 'vllm' || tag === 'ssh-vllm') {
      setSelectedPort(3900);
    } else if (tag === 'triton' || tag === 'ssh-triton') {
      setSelectedPort(3000);
    }
  }, [instance?.instance_tag]);

  if (isLoading) {
    return (
      <div className="container py-8 flex justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertDescription>
            Instance not found. It may have been terminated or the ID is invalid.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const proxyEndpoint = getProxyEndpoint('', selectedPort);
  const tagPorts = getTagPorts();

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6" />
            Endpoint
          </h1>
          <p className="text-muted-foreground mt-1">
            Access your instance through the proxy endpoint
          </p>
        </div>
        {instance.instance_tag && (
          <Badge variant="secondary">
            {InstanceTagLabel[instance.instance_tag] || instance.instance_tag}
          </Badge>
        )}
      </div>

      {/* Proxy Endpoint Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Proxy Endpoint
          </CardTitle>
          <CardDescription>
            Your instance is accessible through the Float16 proxy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Instance ID Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Instance ID</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted p-2 rounded text-sm font-mono truncate">
                  {parentTaskId}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(parentTaskId || '', 'instanceId')}
                >
                  {copiedField === 'instanceId' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Port (3000-4000)</Label>
              <Input
                type="number"
                min={MIN_PORT}
                max={MAX_PORT}
                value={selectedPort}
                onChange={(e) => {
                  const port = parseInt(e.target.value);
                  if (port >= MIN_PORT && port <= MAX_PORT) {
                    setSelectedPort(port);
                  }
                }}
                className="font-mono"
              />
            </div>
          </div>

          {/* Endpoint URL */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Endpoint URL</Label>
            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg text-sm font-mono break-all pr-24">
                {proxyEndpoint}
              </pre>
              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(proxyEndpoint, 'endpoint')}
                >
                  {copiedField === 'endpoint' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  asChild
                >
                  <a href={proxyEndpoint} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Port Selection */}
          <div className="space-y-3">
            <Label className="text-sm text-muted-foreground">Quick Select Port</Label>
            <div className="flex flex-wrap gap-2">
              {tagPorts.map((p) => (
                <Button
                  key={p.port}
                  variant={selectedPort === p.port ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPort(p.port)}
                  className="font-mono"
                >
                  :{p.port}
                  <span className="ml-2 text-xs opacity-70">{p.name}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Examples */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Usage Examples</CardTitle>
          <CardDescription>How to access your endpoint</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* cURL Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">cURL</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`curl ${getProxyEndpoint('/health', selectedPort)}`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`curl ${getProxyEndpoint('/health', selectedPort)}`, 'curl')}
              >
                {copiedField === 'curl' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>

          {/* Python Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Python</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`import requests

response = requests.get("${getProxyEndpoint('/health', selectedPort)}")
print(response.json())`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`import requests\n\nresponse = requests.get("${getProxyEndpoint('/health', selectedPort)}")\nprint(response.json())`, 'python')}
              >
                {copiedField === 'python' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>

          {/* JavaScript Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">JavaScript</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`const response = await fetch("${getProxyEndpoint('/health', selectedPort)}");
const data = await response.json();
console.log(data);`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`const response = await fetch("${getProxyEndpoint('/health', selectedPort)}");\nconst data = await response.json();\nconsole.log(data);`, 'js')}
              >
                {copiedField === 'js' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Response Types - JSON vs SSE */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Response Types</CardTitle>
          <CardDescription>Switch between JSON and Server-Sent Events (SSE) streaming</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Explanation */}
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The proxy automatically detects and handles both JSON and SSE responses based on the <code className="bg-muted px-1 rounded">Accept</code> header.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-3 border rounded-lg">
                <div className="font-medium text-sm mb-1">JSON Response</div>
                <div className="text-xs text-muted-foreground">
                  Default behavior. Returns complete response after generation finishes.
                </div>
              </div>
              <div className="p-3 border rounded-lg border-blue-500/30 bg-blue-500/5">
                <div className="font-medium text-sm mb-1 text-blue-600">SSE Streaming</div>
                <div className="text-xs text-muted-foreground">
                  Real-time token streaming. Requires <code className="bg-muted px-1 rounded">Accept: text/event-stream</code> header.
                </div>
              </div>
            </div>
          </div>

          {/* JSON Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">JSON Response (Default)</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`curl -X POST ${getProxyEndpoint('/v1/chat/completions', selectedPort)} \\
  -H "Content-Type: application/json" \\
  -d '{"model": "your-model", "messages": [{"role": "user", "content": "Hello"}]}'`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`curl -X POST ${getProxyEndpoint('/v1/chat/completions', selectedPort)} \\\n  -H "Content-Type: application/json" \\\n  -d '{"model": "your-model", "messages": [{"role": "user", "content": "Hello"}]}'`, 'curlJson')}
              >
                {copiedField === 'curlJson' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>

          {/* SSE Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">SSE Streaming Response</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`curl -X POST ${getProxyEndpoint('/v1/chat/completions', selectedPort)} \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{"model": "your-model", "messages": [{"role": "user", "content": "Hello"}], "stream": true}'`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`curl -X POST ${getProxyEndpoint('/v1/chat/completions', selectedPort)} \\\n  -H "Content-Type: application/json" \\\n  -H "Accept: text/event-stream" \\\n  -d '{"model": "your-model", "messages": [{"role": "user", "content": "Hello"}], "stream": true}'`, 'curlSse')}
              >
                {copiedField === 'curlSse' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>

          {/* Python SSE Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Python SSE Streaming</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`import requests

response = requests.post(
    "${getProxyEndpoint('/v1/chat/completions', selectedPort)}",
    headers={
        "Content-Type": "application/json",
        "Accept": "text/event-stream"
    },
    json={
        "model": "your-model",
        "messages": [{"role": "user", "content": "Hello"}],
        "stream": True
    },
    stream=True
)

for line in response.iter_lines():
    if line:
        print(line.decode('utf-8'))`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`import requests\n\nresponse = requests.post(\n    "${getProxyEndpoint('/v1/chat/completions', selectedPort)}",\n    headers={\n        "Content-Type": "application/json",\n        "Accept": "text/event-stream"\n    },\n    json={\n        "model": "your-model",\n        "messages": [{"role": "user", "content": "Hello"}],\n        "stream": True\n    },\n    stream=True\n)\n\nfor line in response.iter_lines():\n    if line:\n        print(line.decode('utf-8'))`, 'pythonSse')}
              >
                {copiedField === 'pythonSse' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>

          {/* JavaScript SSE Example */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">JavaScript SSE Streaming</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`const response = await fetch("${getProxyEndpoint('/v1/chat/completions', selectedPort)}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Accept": "text/event-stream"
  },
  body: JSON.stringify({
    model: "your-model",
    messages: [{ role: "user", content: "Hello" }],
    stream: true
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  console.log(decoder.decode(value));
}`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-7"
                onClick={() => handleCopy(`const response = await fetch("${getProxyEndpoint('/v1/chat/completions', selectedPort)}", {\n  method: "POST",\n  headers: {\n    "Content-Type": "application/json",\n    "Accept": "text/event-stream"\n  },\n  body: JSON.stringify({\n    model: "your-model",\n    messages: [{ role: "user", content: "Hello" }],\n    stream: true\n  })\n});\n\nconst reader = response.body.getReader();\nconst decoder = new TextDecoder();\n\nwhile (true) {\n  const { done, value } = await reader.read();\n  if (done) break;\n  console.log(decoder.decode(value));\n}`, 'jsSse')}
              >
                {copiedField === 'jsSse' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p>
              <strong>Proxy Information:</strong>
            </p>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Only ports <strong>3000-4000</strong> are accessible through the proxy</li>
              <li>The endpoint format is: <code className="text-xs bg-muted px-1 rounded">{PROXY_BASE_URL}/{'${instance_id}'}/{'${port}'}/{'${path}'}</code></li>
              <li>Make sure your application is listening on the selected port inside the instance</li>
              <li>HTTPS is handled by the proxy - your internal service can use HTTP</li>
              <li>For SSE streaming, include <code className="text-xs bg-muted px-1 rounded">Accept: text/event-stream</code> header</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default GPUInstanceEndpoint;
